"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useCartStore } from "@/store/cart";

type CartUIContextValue = {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
};

const CartUIContext = createContext<CartUIContextValue | null>(null);

export function CartUIProvider({ children }: { children: React.ReactNode }) {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);

  useEffect(() => {
    // Hydrate persisted cart state on the client (store is configured with skipHydration).
    useCartStore.persist.rehydrate();
  }, []);

  const openCart = useCallback(() => {
    lastActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setIsCartOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleCart = useCallback(() => {
    setIsCartOpen((prev) => {
      if (!prev) {
        lastActiveElementRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (!isCartOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isCartOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isCartOpen) {
      const el = lastActiveElementRef.current;
      requestAnimationFrame(() => el?.focus?.());
    }
    wasOpenRef.current = isCartOpen;
  }, [isCartOpen]);

  useEffect(() => {
    if (lastPathnameRef.current !== pathname) {
      lastPathnameRef.current = pathname;
      if (isCartOpen) {
        lastActiveElementRef.current = null;
        setIsCartOpen(false);
      }
    }
  }, [isCartOpen, pathname]);

  const value = useMemo<CartUIContextValue>(
    () => ({ isCartOpen, openCart, closeCart, toggleCart }),
    [isCartOpen, openCart, closeCart, toggleCart],
  );

  return (
    <CartUIContext.Provider value={value}>{children}</CartUIContext.Provider>
  );
}

export function useCartUI(): CartUIContextValue {
  const ctx = useContext(CartUIContext);
  if (!ctx) {
    throw new Error("useCartUI must be used within <CartUIProvider />");
  }
  return ctx;
}
